import { useList } from '@refinedev/core';
import { useMemo } from 'react';

import { AvailableResources } from '@/config';
import { DEFAULT_CACHE_TIME, DEFAULT_STALE_TIME } from '@/constants';
import useGetRegions from '@/hooks/useGetRegions';

import type { Props, ScopeDetails, SimplifiedCycle } from './types';

/**
 * Resolves the display names and planning crop of the scope the route points
 * at. Lookup, not state: the ids come from the URL, and everything derived
 * from them is read back out of the two session-stable lists.
 *
 * The cycle list is keyed exactly as `useGetCropSeasonOptions` keys it, and
 * the regions list is the one `useGetRegions` already serves, so this shares
 * both caches with the season selector rather than issuing requests of its own.
 */
const useGetScopeDetails = ({
  regionId,
  seasonId,
  enabled = true
}: Props): ScopeDetails => {
  const regionsEnabled = Boolean(enabled && regionId);
  const cyclesEnabled = Boolean(enabled && regionId && seasonId);

  const { data: regions, isLoading: isLoadingRegions } = useGetRegions({
    enabled: regionsEnabled
  });

  const {
    data: cycles,
    isLoading: isLoadingCycles,
    fetchStatus
  } = useList<SimplifiedCycle>({
    resource: AvailableResources.SIMPLIFIED_CYCLES,
    pagination: {
      mode: 'off'
    },
    meta: {
      params: {
        region_id: regionId
      }
    },
    queryOptions: {
      enabled: cyclesEnabled,
      // Session-stable: only a season write changes this list, and that
      // invalidates the cache (see `useInvalidateCropSeasonOptions`).
      staleTime: DEFAULT_STALE_TIME,
      // Must outlive the stale time, or an entry is dropped while still fresh.
      cacheTime: DEFAULT_CACHE_TIME,
      queryKey: [AvailableResources.SIMPLIFIED_CYCLES, regionId]
    }
  });

  const regionName = useMemo(
    () => regions.find(region => region.id === regionId)?.name,
    [regions, regionId]
  );

  const cycle = useMemo(
    () => cycles?.data?.find(cycle => cycle.id === seasonId),
    [cycles, seasonId]
  );

  return {
    regionName,
    cropSeasonName: cycle?.name,
    cycleCropId: cycle?.crop_id,
    // Each half is gated on its own query being enabled, because react-query
    // reports a *disabled* query as `isLoading: true` — it has no data and
    // never will. Passing that through would tell consumers to wait for a
    // request that was never made.
    isLoading:
      (regionsEnabled && isLoadingRegions) ||
      (cyclesEnabled && (isLoadingCycles || fetchStatus === 'fetching'))
  };
};

export default useGetScopeDetails;
